import {
  BulkEditEmployeesProfilesRequest,
  BulkEditEmployeesLabelsRequest,
  BulkDeleteEmployeesRequest,
  ResponseErrorDto,
  LabelType,
} from '@interfaces/models';
import {
  bulkEditEmployeeProfiles,
  bulkEditEmployeeLabels,
  bulkEditEmployeeDelete,
} from '@api/employees/employees.service';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useGetEmployeeProfiles } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { useGetLabels } from '@shared/hooks';
import { toast } from '@ui/components';
import { useState } from 'react';

export const useEmployeeBulkActions = ({
  onEmployeeRemoveSuccess,
}: {
  onEmployeeRemoveSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { employeeProfilesOptions } = useGetEmployeeProfiles();
  const { labelsOptions } = useGetLabels(LabelType.EMPLOYEE);

  const [isAddDepartmentOpen, setIsAddDepartmentOpen] = useState(false);
  const [isAddProfileOpen, setIsAddProfileOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { mutate: bulkEditEmployeeProfilesMutation, isPending: isPendingProfiles } = useMutation({
    mutationFn: ({ employeeIds, profiles }: BulkEditEmployeesProfilesRequest) =>
      bulkEditEmployeeProfiles({ employeeIds, profiles }),
    onSuccess: () => {
      setIsAddProfileOpen(false);
      queryClient.invalidateQueries({ queryKey: ['employees-table'] });
      toast.success(t('EMPLOYEES.TABLE_SUCCESS_UPDATE_EMPLOYEE_PROFILES'));
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const { mutate: bulkEditEmployeeLabelsMutation, isPending: isPendingLabels } = useMutation({
    mutationFn: ({ employeeIds, labels }: BulkEditEmployeesLabelsRequest) =>
      bulkEditEmployeeLabels({ employeeIds, labels }),
    onSuccess: () => {
      setIsAddDepartmentOpen(false);
      queryClient.invalidateQueries({ queryKey: ['employees-table'] });
      toast.success(t('EMPLOYEES.BULK_ADD_DEPARTMENT_SUCCESS'));
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const { mutate: bulkEditEmployeesDeleteMutation, isPending: isPendingDelete } = useMutation({
    mutationFn: ({ employeeIds }: BulkDeleteEmployeesRequest) =>
      bulkEditEmployeeDelete({ employeeIds }),
    onSuccess: () => {
      setIsDeleteOpen(false);
      queryClient.invalidateQueries({ queryKey: ['employees-table'] });
      toast.success(t('EMPLOYEES.BULK_DELETE_DIALOG_SUCCESS'));
      onEmployeeRemoveSuccess();
    },
    onError: () => {
      toast.error(t('API_ERRORS.DEFAULT'));
    },
  });

  return {
    bulkEditEmployeeProfilesMutation,
    bulkEditEmployeesDeleteMutation,
    bulkEditEmployeeLabelsMutation,
    setIsAddDepartmentOpen,
    setIsAddProfileOpen,
    setIsDeleteOpen,
    employeeProfilesOptions,
    isAddDepartmentOpen,
    isPendingProfiles,
    isAddProfileOpen,
    isPendingLabels,
    isPendingDelete,
    labelsOptions,
    isDeleteOpen,
  };
};
